import type { KeyboardEvent, ReactNode } from "react";
import { useEffect, useMemo, useRef } from "react";

import {
  cellSelectionFeature,
  createColumnHelper,
  createSortedRowModel,
  functionalUpdate,
  metaHelper,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_text,
  tableFeatures,
  useTable,
  type CellSelectionDirection,
  type ColumnDef,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";

import { Checkbox } from "@beweegbaas/ui/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  getTableRowTarget,
} from "@beweegbaas/ui/components/ui/table";
import { cn } from "@beweegbaas/ui/lib/utils";
import { IconChevronUp } from "@tabler/icons-react";

/** Per-column presentation hints, typed via the feature registry's `metaHelper`. */
export interface DataTableColumnMeta {
  /** Horizontal alignment of header + cells. */
  align?: "start" | "end";
  /** Marks an actions column: content right-aligned in a flex wrapper. */
  action?: boolean;
  /**
   * The primary column (name/activity) that may break onto more lines so the table fits;
   * every other cell stays on one line. It claims no spare width, so the browser only wraps it
   * (and `whitespace-normal` headers) once the table runs out of room.
   */
  wrap?: boolean;
  /**
   * A click anywhere on the row activates this column's link, so the row navigates. Selectable
   * tables get the same for their checkbox column.
   */
  rowTarget?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, text: sortFn_text },
  rowSelectionFeature,
  cellSelectionFeature,
  columnMeta: metaHelper<DataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

/** Bound column helper — the only import call sites need to define typed columns. */
export function createDataTableColumnHelper<TData extends RowData>() {
  return createColumnHelper<DataTableFeatures, TData>();
}

export type DataTableColumns<TData extends RowData> = readonly ColumnDef<
  DataTableFeatures,
  TData,
  any
>[];

export interface DataTableProps<TData extends RowData> {
  columns: DataTableColumns<TData>;
  data: readonly TData[];
  /** Stable row id — required for selection; falls back to the row index. */
  getRowId?: (row: TData) => string;
  "aria-label"?: string;
  className?: string;
  size?: "default" | "small";
  /** Card chrome: white surface, ring border, rounded corners, inset cell padding. */
  card?: boolean;
  /** Full-bleed table whose first/last cells align with the page container gutter. */
  alignToContainer?: boolean;
  /** Rendered as a full-width row when there are no rows. */
  empty?: ReactNode;
  /** Controlled sorting (typically URL-driven). Data is assumed to arrive sorted (`manualSorting`). */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** Set `false` to let TanStack sort client-side while keeping controlled state. */
  manualSorting?: boolean;
  /** Controlled multi-selection by row id; providing the change handler enables the checkbox column. */
  selectedIds?: string[];
  onSelectedIdsChange?: (ids: string[]) => void;
  selectAllLabel?: string;
  getSelectLabel?: (row: TData) => string;
  getRowClassName?: (row: TData) => string | undefined;
  /**
   * Arrow-key cell focus; Enter/Space activates the focused cell's link or button, or else the
   * row's `rowTarget` link or checkbox.
   * Navigation is per the TanStack v9 cell-selection guide: the feature ships no keydown
   * handling, so this keydown handler (scoped to the table element) drives its imperative APIs.
   */
  enableKeyboardNavigation?: boolean;
}

const KEY_TO_DIRECTION: Record<string, CellSelectionDirection> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
};

export function DataTable<TData extends RowData>({
  columns,
  data,
  getRowId,
  className,
  size = "default",
  card = false,
  alignToContainer = false,
  empty,
  sorting,
  onSortingChange,
  manualSorting,
  selectedIds,
  onSelectedIdsChange,
  selectAllLabel,
  getSelectLabel,
  getRowClassName,
  enableKeyboardNavigation = true,
  "aria-label": ariaLabel,
}: DataTableProps<TData>) {
  const selectable = onSelectedIdsChange !== undefined;

  const allColumns = useMemo(() => {
    if (!selectable) return columns as ColumnDef<DataTableFeatures, TData, any>[];
    const selectColumn: ColumnDef<DataTableFeatures, TData, any> = {
      id: "__select",
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onCheckedChange={() => table.toggleAllRowsSelected()}
          aria-label={selectAllLabel ?? "Select all rows"}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(checked) => row.toggleSelected(!!checked)}
          aria-label={getSelectLabel?.(row.original) ?? "Select row"}
        />
      ),
      meta: { cellClassName: "w-12", rowTarget: true },
    };
    return [selectColumn, ...columns];
  }, [columns, selectable, selectAllLabel, getSelectLabel]);

  const rowSelection = useMemo<RowSelectionState>(() => {
    return Object.fromEntries((selectedIds ?? []).map((id) => [id, true]));
  }, [selectedIds]);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    const next = functionalUpdate(updater, rowSelection);
    onSelectedIdsChange?.(
      Object.entries(next)
        .filter(([, selected]) => selected)
        .map(([id]) => id),
    );
  };

  const table = useTable<DataTableFeatures, TData>({
    features: dataTableFeatures,
    data: data as TData[],
    columns: allColumns,
    ...(getRowId ? { getRowId } : {}),
    defaultColumn: { enableSorting: false },
    enableSortingRemoval: false,
    manualSorting: manualSorting ?? onSortingChange !== undefined,
    enableRowSelection: selectable,
    state: {
      ...(sorting !== undefined ? { sorting } : {}),
      ...(selectable ? { rowSelection } : {}),
    },
    ...(onSortingChange ? { onSortingChange } : {}),
    ...(selectable ? { onRowSelectionChange: handleRowSelectionChange } : {}),
  });

  const rows = table.getRowModel().rows;
  const leafColumnCount = table.getAllLeafColumns().length;

  const focusedCell = enableKeyboardNavigation ? table.getFocusedCell() : undefined;
  const focusedCellId = focusedCell?.id;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusedCellId) return;
    containerRef.current
      ?.querySelector(`[data-cell-id="${CSS.escape(focusedCellId)}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [focusedCellId]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    if (!enableKeyboardNavigation) return;
    // Never hijack keys while an input, button, or link inside a cell has focus.
    if (event.target !== event.currentTarget) return;

    const direction = KEY_TO_DIRECTION[event.key];
    if (direction) {
      event.preventDefault();
      const focused = table.getFocusedCell();
      if (!focused) {
        const firstRow = rows[0];
        const firstColumn = table.getAllLeafColumns()[0];
        if (firstRow && firstColumn) table.setFocusedCell(firstRow.id, firstColumn.id);
      } else {
        table.moveCellSelection(direction);
      }
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      const focused = table.getFocusedCell();
      if (!focused) return;
      const cellElement = containerRef.current?.querySelector(
        `[data-cell-id="${CSS.escape(focused.id)}"]`,
      );
      const rowElement = cellElement?.closest("tr");
      const target =
        cellElement?.querySelector<HTMLElement>("a, button") ??
        (rowElement ? getTableRowTarget(rowElement) : null);
      if (target) {
        event.preventDefault();
        target.click();
      }
      return;
    }
    if (event.key === "Escape") {
      table.resetCellSelection(true);
    }
  };

  // Shared per-cell presentation for a variant combination.
  const cellVariantClass = cn(
    alignToContainer && "first:pl-(--container-gutter) last:pr-(--container-gutter)",
    card && "px-3 first:pl-4 last:pr-4",
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative min-w-0",
        "[--dt-surface:var(--color-background)]",
        alignToContainer && "mx-auto w-full max-w-(--container-max-width)",
        card && "[--dt-surface:var(--color-card)] bg-card ring-border rounded-lg ring-1",
        className,
      )}
    >
      <Table
        aria-label={ariaLabel}
        containerClassName={cn(card && "rounded-lg")}
        tabIndex={enableKeyboardNavigation ? 0 : undefined}
        onKeyDown={handleKeyDown}
        className="focus-visible:outline-none"
      >
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const column = header.column;
                const meta = column.columnDef.meta;
                const canSort = column.getCanSort();
                const sorted = column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    data-table-action={meta?.action ? "" : undefined}
                    aria-sort={
                      canSort
                        ? sorted === "asc"
                          ? "ascending"
                          : sorted === "desc"
                            ? "descending"
                            : "none"
                        : undefined
                    }
                    className={cn(
                      size === "small" && "h-8 px-2",
                      cellVariantClass,
                      meta?.align === "end" && "text-right",
                      meta?.action && "z-[1] text-right",
                      meta?.headerClassName,
                    )}
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        className="group hover:text-foreground/70 -mx-1 inline-flex cursor-pointer items-center gap-2 rounded px-1 transition"
                        onClick={column.getToggleSortingHandler()}
                      >
                        <table.FlexRender header={header} />
                        <IconChevronUp
                          aria-hidden
                          className={cn(
                            "text-muted-foreground size-4 opacity-50 transition group-hover:opacity-100",
                            sorted && "opacity-100",
                            sorted === "desc" && "rotate-180",
                          )}
                        />
                      </button>
                    ) : (
                      <table.FlexRender header={header} />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className={cn(card && "[&_tr:last-child]:border-0")}>
          {rows.length === 0 && empty !== undefined ? (
            <TableRow>
              <TableCell colSpan={leafColumnCount} className={cellVariantClass}>
                <div className="text-muted-foreground flex flex-col items-start gap-3 p-4">
                  {empty}
                </div>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const isSelected = row.getIsSelected();
              return (
                <TableRow
                  key={row.id}
                  data-row-id={row.id}
                  data-state={isSelected ? "selected" : undefined}
                  className={getRowClassName?.(row.original)}
                >
                  {row.getAllCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    const isFocused = enableKeyboardNavigation && cell.getIsFocused();
                    return (
                      <TableCell
                        key={cell.id}
                        data-cell-id={enableKeyboardNavigation ? cell.id : undefined}
                        data-table-action={meta?.action ? "" : undefined}
                        rowTarget={meta?.rowTarget}
                        className={cn(
                          size === "small" && "h-10 px-2",
                          cellVariantClass,
                          isSelected && "font-medium",
                          meta?.wrap && "min-w-[16ch] whitespace-normal",
                          meta?.align === "end" && "text-right",
                          meta?.action && "z-[1] text-right",
                          isFocused && "ring-ring rounded-sm ring-2 -ring-offset-2",
                          meta?.cellClassName,
                        )}
                      >
                        {meta?.action ? (
                          <div className="flex grow items-center justify-end gap-2">
                            <table.FlexRender cell={cell} />
                          </div>
                        ) : (
                          <table.FlexRender cell={cell} />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
